import React, { useState, useEffect } from "react";
import { X, Box, Loader2, Check, Sparkles } from "lucide-react";

const SaveSpriteDialog = ({
  isOpen,
  onClose,
  onSave,
  initialName = "",
  spriteData = null,
  theme = "dark", // "dark" | "light" | "jr"
}) => {
  const [name, setName] = useState(initialName || "");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const isJr = theme === "jr";
  const isLight = theme === "light" || isJr;

  useEffect(() => {
    if (isOpen) {
      setName(initialName || spriteData?.defaultName || "Unnamed Sprite");
      setDescription("");
      setError("");
      setIsSaving(false);
    }
  }, [isOpen, initialName, spriteData]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim()) {
      setError("Please enter a name for this sprite.");
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        spriteCode: spriteData?.spriteCode,
        thumbnail: spriteData?.thumbnail || null,
      });
      onClose();
    } catch (err) {
      console.error("Failed to save sprite:", err);
      setError(err.message || "Failed to save sprite. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        className={`w-full max-w-md shadow-2xl overflow-hidden font-sans ${
          isJr
            ? "rounded-3xl bg-[#FAF6EE] border-4 border-[#E6C896] text-[#3D2610]"
            : isLight
            ? "rounded-2xl bg-white ring-1 ring-neutral-200 text-neutral-800 border-0"
            : "rounded-2xl bg-[#13151b] ring-1 ring-[#27272a] text-white shadow-[0_25px_60px_rgba(0,0,0,0.9)] border-0"
        }`}
      >
        {/* Header */}
        <div
          className={`px-5 py-4 flex items-center justify-between ${
            isJr
              ? "bg-gradient-to-r from-[#FFF3D1] via-[#FFE8AD] to-[#FFDF8E] border-b-2 border-[#E6C896]"
              : isLight
              ? "bg-neutral-50 border-b border-neutral-200 border-0"
              : "bg-[#0c0d12] border-b border-[#222226] border-0"
          }`}
        >
          <div className="flex items-center gap-2.5">
            <div
              className={
                isJr
                  ? "w-10 h-10 rounded-2xl bg-gradient-to-b from-[#A855F7] to-[#7E22CE] border-2 border-[#6B21A8] shadow-[0_2px_0_#581C87] flex items-center justify-center text-white"
                  : "w-9 h-9 rounded-xl bg-purple-600/15 ring-1 ring-purple-500/25 flex items-center justify-center text-purple-400"
              }
            >
              <Box className="w-5 h-5" />
            </div>
            <div>
              <h3
                className={`font-black text-base leading-tight ${
                  isJr
                    ? "text-[#3D2610]"
                    : isLight
                    ? "text-neutral-900"
                    : "text-white"
                }`}
              >
                Save Sprite to Backpack
              </h3>
              <p
                className={`text-xs font-semibold ${
                  isJr
                    ? "text-[#92400E]"
                    : isLight
                    ? "text-neutral-500"
                    : "text-neutral-400"
                }`}
              >
                Reuse this character and its scripts in any project
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className={
              isJr
                ? "w-8 h-8 rounded-xl bg-[#FEE2E2] hover:bg-[#FECACA] text-[#DC2626] border-2 border-[#F87171] flex items-center justify-center transition-colors cursor-pointer"
                : `w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer border-0 ${
                    isLight
                      ? "bg-neutral-100 hover:bg-neutral-200 text-neutral-500"
                      : "bg-white/5 hover:bg-white/10 text-neutral-300"
                  }`
            }
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Sprite Preview Card */}
          <div
            className={`p-3.5 rounded-2xl flex items-center gap-3.5 ${
              isJr
                ? "bg-white border-2 border-[#E2C285] shadow-[0_3px_0_#EDE0C8] text-[#3D2610]"
                : isLight
                ? "bg-purple-50/80 ring-1 ring-purple-200/80 text-purple-900 border-0"
                : "bg-purple-950/25 ring-1 ring-purple-500/20 text-purple-200 border-0"
            }`}
          >
            <div
              className={
                isJr
                  ? "w-14 h-14 rounded-xl bg-[#FAF5FF] border-2 border-[#E9D5FF] flex items-center justify-center overflow-hidden flex-shrink-0"
                  : "w-14 h-14 rounded-xl bg-white/80 dark:bg-purple-900/30 ring-1 ring-purple-500/30 flex items-center justify-center overflow-hidden flex-shrink-0"
              }
            >
              {spriteData?.thumbnail ? (
                <img
                  src={spriteData.thumbnail}
                  alt={name || "Sprite"}
                  className="w-12 h-12 object-contain"
                />
              ) : (
                <Box className="w-7 h-7 text-purple-400" />
              )}
            </div>

            <div className="flex flex-col overflow-hidden">
              <span className="text-xs font-extrabold truncate">
                {name || spriteData?.defaultName || "Scratch Sprite"}
              </span>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-purple-600/20 text-purple-700 dark:text-purple-300 flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-purple-500" />
                  Costumes & Sounds
                </span>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/40 ring-1 ring-red-200 dark:ring-red-800/40 text-red-600 dark:text-red-300 text-xs font-semibold">
              {error}
            </div>
          )}

          {/* Sprite Name Field */}
          <div className="space-y-1.5 text-left">
            <label
              className={`text-xs font-extrabold flex items-center justify-between ${
                isJr
                  ? "text-[#3D2610]"
                  : isLight
                  ? "text-neutral-700"
                  : "text-neutral-200"
              }`}
            >
              <span>Sprite Name *</span>
              <span className="text-[10px] text-neutral-400 font-normal">
                Max 50 chars
              </span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Hero Character"
              maxLength={50}
              autoFocus
              className={`w-full px-3.5 py-2.5 text-sm font-bold outline-none transition ${
                isJr
                  ? "rounded-2xl bg-white border-2 border-[#E2C285] text-[#3D2610] focus:border-[#F59E0B]"
                  : isLight
                  ? "rounded-xl bg-neutral-50 ring-1 ring-neutral-200 text-neutral-900 focus:ring-2 focus:ring-purple-500 focus:bg-white border-0"
                  : "rounded-xl bg-[#0a0b0f] ring-1 ring-[#27272a] focus:ring-2 focus:ring-purple-500/50 text-white placeholder:text-neutral-500 border-0"
              }`}
            />
          </div>

          {/* Sprite Description Field */}
          <div className="space-y-1.5 text-left">
            <label
              className={`text-xs font-extrabold ${
                isJr
                  ? "text-[#3D2610]"
                  : isLight
                  ? "text-neutral-700"
                  : "text-neutral-200"
              }`}
            >
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Main player sprite with walking and jump animations"
              rows={2}
              maxLength={200}
              className={`w-full px-3.5 py-2 text-sm font-medium outline-none transition resize-none ${
                isJr
                  ? "rounded-2xl bg-white border-2 border-[#E2C285] text-[#3D2610] focus:border-[#F59E0B]"
                  : isLight
                  ? "rounded-xl bg-neutral-50 ring-1 ring-neutral-200 text-neutral-900 focus:ring-2 focus:ring-purple-500 focus:bg-white border-0"
                  : "rounded-xl bg-[#0a0b0f] ring-1 ring-[#27272a] focus:ring-2 focus:ring-purple-500/50 text-white placeholder:text-neutral-500 border-0"
              }`}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2.5 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className={
                isJr
                  ? "px-4 py-2.5 rounded-2xl text-xs font-black bg-white hover:bg-[#FFFDF7] text-[#78350F] border-2 border-[#E2C285] shadow-[0_2px_0_#D4B06A] transition cursor-pointer"
                  : `px-4 py-2.5 rounded-xl text-xs font-bold transition cursor-pointer border-0 ${
                      isLight
                        ? "text-neutral-600 hover:bg-neutral-100"
                        : "text-neutral-300 hover:bg-white/5 hover:text-white"
                    }`
              }
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSaving || !name.trim()}
              className={
                isJr
                  ? "px-5 py-2.5 rounded-2xl text-xs font-black bg-gradient-to-b from-[#A855F7] to-[#7E22CE] hover:from-[#9333EA] hover:to-[#6B21A8] active:translate-y-[1px] text-white border-2 border-[#6B21A8] shadow-[0_3px_0_#581C87] flex items-center gap-2 transition cursor-pointer disabled:opacity-50"
                  : "px-5 py-2.5 rounded-xl text-xs font-extrabold bg-purple-600 hover:bg-purple-500 active:scale-95 text-white shadow-md shadow-purple-600/30 flex items-center gap-2 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed border-0"
              }
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>Save to Backpack</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaveSpriteDialog;
